import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import * as Switch from '@radix-ui/react-switch';
import { AuthContext } from '../contexts/AuthContext';
import api, { apiErrorMessage } from '../services/api';
import { categories } from '../utils/staticData';
import FloatingInput from '../components/ui/FloatingInput';
import SegmentedControl from '../components/ui/SegmentedControl';

/* ------------------------------------------------------------------ */
/*  Step definitions                                                   */
/* ------------------------------------------------------------------ */
const STEPS = [
  { label: 'Basics', icon: '1' },
  { label: 'When & Where', icon: '2' },
  { label: 'Details', icon: '3' },
  { label: 'Review', icon: '4' },
];

/* ------------------------------------------------------------------ */
/*  Step indicator bar                                                 */
/* ------------------------------------------------------------------ */
function StepIndicator({ currentStep, onStepClick }) {
  return (
    <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-soft border-b border-surface-100">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => {
            const isComplete = idx < currentStep;
            const isCurrent = idx === currentStep;
            return (
              <React.Fragment key={step.label}>
                <button
                  type="button"
                  onClick={() => idx < currentStep && onStepClick(idx)}
                  disabled={idx > currentStep}
                  className="flex items-center gap-2 group"
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-caption font-bold transition-all duration-200 ${
                      isComplete
                        ? 'bg-primary-500 text-white'
                        : isCurrent
                        ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500/30'
                        : 'bg-surface-100 text-surface-400'
                    }`}
                  >
                    {isComplete ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <span
                    className={`hidden sm:inline text-body-sm font-medium transition-colors ${
                      isCurrent ? 'text-primary-700' : isComplete ? 'text-surface-700' : 'text-surface-400'
                    }`}
                  >
                    {step.label}
                  </span>
                </button>
                {idx < STEPS.length - 1 && (
                  <div className="flex-1 mx-2 sm:mx-3">
                    <div className={`h-0.5 rounded-full transition-colors duration-300 ${idx < currentStep ? 'bg-primary-500' : 'bg-surface-200'}`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Review field helper                                                */
/* ------------------------------------------------------------------ */
function ReviewField({ label, value, onEdit }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between py-2.5">
      <div>
        <p className="text-caption text-surface-400 uppercase tracking-wider">{label}</p>
        <p className="text-body-sm text-surface-800 mt-0.5">{value}</p>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-caption font-semibold text-primary-500 hover:text-primary-700 transition-colors"
        >
          Edit
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
function CreateEvent() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cliqqType: 'normal',
    privacy: 'public',
    privateScope: 'invite',
    requireApproval: false,
    maxAttendees: '',
    ageMin: '',
    ageMax: '',
    location: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    addressVisibility: 'public',
    isOnline: false,
    secondaryLocation: '',
    secondaryAddress: '',
    secondaryTime: '',
    secondaryDescription: '',
    startTime: '',
    endTime: '',
    joinCloseTime: '',
    cost: '',
    categoryId: '',
    customTags: '',
    chatOption: 'twoWay',
    links: '',
    imageUrls: '',
    imageUrl: '',
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (new Date(formData.startTime) >= new Date(formData.endTime)) {
        toast.error('End time must be after start time');
        setSubmitting(false);
        return;
      }

      if (!formData.categoryId) {
        toast.error('Please choose a category');
        setSubmitting(false);
        setStep(0);
        return;
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        type: formData.cliqqType,
        privacy: formData.privacy,
        private_scope: formData.privateScope,
        require_approval: formData.requireApproval,
        max_attendees: formData.maxAttendees ? parseInt(formData.maxAttendees, 10) || null : null,
        age_min: formData.ageMin ? parseInt(formData.ageMin, 10) || null : null,
        age_max: formData.ageMax ? parseInt(formData.ageMax, 10) || null : null,
        location: formData.isOnline ? (formData.location || 'Online') : formData.location,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        address_visibility: formData.addressVisibility,
        is_online: formData.isOnline,
        secondary_location: formData.secondaryLocation,
        secondary_address: formData.secondaryAddress,
        secondary_time: formData.secondaryTime ? new Date(formData.secondaryTime).toISOString() : null,
        secondary_description: formData.secondaryDescription,
        start_time: new Date(formData.startTime).toISOString(),
        end_time: new Date(formData.endTime).toISOString(),
        join_close_time: formData.joinCloseTime ? new Date(formData.joinCloseTime).toISOString() : null,
        cost: formData.cost,
        category_id: formData.categoryId ? parseInt(formData.categoryId, 10) || null : null,
        custom_tags: formData.customTags.split(',').map((t) => t.trim()).filter(Boolean),
        chat_option: formData.chatOption,
        links: formData.links.split(',').map((l) => l.trim()).filter(Boolean),
        image_urls: formData.imageUrls
          ? formData.imageUrls.split(',').map((u) => u.trim()).filter(Boolean)
          : formData.imageUrl
          ? [formData.imageUrl]
          : [],
      };

      const token = localStorage.getItem('token');
      if (!token && !currentUser) {
        toast.error('You must be logged in to create an event');
        setSubmitting(false);
        return;
      }

      const response = await api.post('/api/events', eventData);
      const body = response.data;
      const created = (body && body.data) ? body.data : body;
      const newId = created.id || created.ID;
      toast.success('Event created successfully!');
      setSubmitting(false);
      setTimeout(() => {
        navigate(newId ? `/events/${newId}` : '/');
      }, 800);
    } catch (apiError) {
      // Rejections are APIError instances (no `.response`); read `.status`.
      if (apiError.status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error(apiErrorMessage(apiError, 'Failed to create event. Please try again.'));
      }
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    // Validate current step
    if (step === 0) {
      if (!formData.title.trim()) { toast.error('Title is required'); return; }
      if (!formData.description.trim()) { toast.error('Description is required'); return; }
    }
    if (step === 1) {
      if (!formData.startTime) { toast.error('Start time is required'); return; }
      if (!formData.endTime) { toast.error('End time is required'); return; }
      if (new Date(formData.startTime) >= new Date(formData.endTime)) {
        toast.error('End time must be after start time'); return;
      }
      if (!formData.isOnline) {
        if (!formData.location.trim()) { toast.error('Location is required'); return; }
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const getCategoryName = (id) => {
    const cat = categories.find((c) => String(c.id) === String(id));
    return cat ? cat.name : '';
  };

  const formatReviewDate = (dateStr) => {
    if (!dateStr) return '';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateStr));
  };

  /* Step content animation variants */
  const stepVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Step Indicator */}
      <StepIndicator currentStep={step} onStepClick={setStep} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* ============================== */}
            {/* STEP 0: Basics                 */}
            {/* ============================== */}
            {step === 0 && (
              <motion.div
                key="step-0"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <h1 className="text-h1 text-surface-900">Create a New CliQQ</h1>
                  <p className="text-body text-surface-500 mt-1">Start with the basics</p>
                </div>

                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
                  <FloatingInput
                    label="CliQQ Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />

                  <div className="relative">
                    <textarea
                      id="description"
                      name="description"
                      rows={5}
                      value={formData.description}
                      onChange={handleChange}
                      required
                      placeholder=" "
                      className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-5 pb-2 px-4 focus:ring-2 focus:bg-white focus:outline-none focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-body placeholder-transparent resize-none"
                    />
                    <label
                      htmlFor="description"
                      className="absolute left-4 transition-all duration-200 pointer-events-none select-none peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400 peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600 top-2 text-caption text-surface-500"
                    >
                      Description <span className="text-error-500">*</span>
                    </label>
                  </div>

                  <div>
                    <label htmlFor="categoryId" className="block text-body-sm font-semibold text-surface-700 mb-2">
                      Category
                    </label>
                    <select
                      id="categoryId"
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleChange}
                      className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <FloatingInput
                    label="Image URL"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleChange}
                  />
                </section>
              </motion.div>
            )}

            {/* ============================== */}
            {/* STEP 1: When & Where           */}
            {/* ============================== */}
            {step === 1 && (
              <motion.div
                key="step-1"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <h1 className="text-h1 text-surface-900">When & Where</h1>
                  <p className="text-body text-surface-500 mt-1">Set the date, time, and location</p>
                </div>

                {/* Date & Time */}
                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
                  <h2 className="text-h3 text-surface-900">Date & Time</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="startTime" className="block text-body-sm font-semibold text-surface-700 mb-2">
                        Start Date & Time <span className="text-error-500">*</span>
                      </label>
                      <input
                        id="startTime"
                        type="datetime-local"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleChange}
                        required
                        className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label htmlFor="endTime" className="block text-body-sm font-semibold text-surface-700 mb-2">
                        End Date & Time <span className="text-error-500">*</span>
                      </label>
                      <input
                        id="endTime"
                        type="datetime-local"
                        name="endTime"
                        value={formData.endTime}
                        onChange={handleChange}
                        required
                        className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                      />
                    </div>
                  </div>
                </section>

                {/* Location */}
                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
                  <div className="flex items-center justify-between">
                    <h2 className="text-h3 text-surface-900">Location</h2>
                    <div className="flex items-center gap-2">
                      <label htmlFor="isOnline" className="text-body-sm text-surface-600">Online event</label>
                      <Switch.Root
                        id="isOnline"
                        checked={formData.isOnline || false}
                        onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isOnline: checked }))}
                        className="w-10 h-5.5 bg-surface-200 rounded-full relative data-[state=checked]:bg-primary-500 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 cursor-pointer"
                      >
                        <Switch.Thumb className="block w-4.5 h-4.5 bg-white rounded-full shadow-soft-sm transition-transform duration-200 translate-x-0.5 data-[state=checked]:translate-x-[20px]" />
                      </Switch.Root>
                    </div>
                  </div>

                  {!formData.isOnline && (
                    <div className="space-y-4">
                      <FloatingInput label="Location Name" name="location" value={formData.location} onChange={handleChange} required />
                      <FloatingInput label="Street Address" name="address" value={formData.address} onChange={handleChange} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <FloatingInput label="City" name="city" value={formData.city} onChange={handleChange} />
                        <FloatingInput label="State" name="state" value={formData.state} onChange={handleChange} />
                        <FloatingInput label="Zip Code" name="zipCode" value={formData.zipCode} onChange={handleChange} />
                      </div>
                    </div>
                  )}
                </section>
              </motion.div>
            )}

            {/* ============================== */}
            {/* STEP 2: Details                */}
            {/* ============================== */}
            {step === 2 && (
              <motion.div
                key="step-2"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <h1 className="text-h1 text-surface-900">Details</h1>
                  <p className="text-body text-surface-500 mt-1">Fine-tune your event settings</p>
                </div>

                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
                  <FloatingInput
                    label="Maximum Attendees"
                    name="maxAttendees"
                    type="number"
                    value={formData.maxAttendees}
                    onChange={handleChange}
                    min="1"
                  />

                  <SegmentedControl
                    label="Privacy"
                    name="privacy"
                    value={formData.privacy}
                    onChange={(val) => setFormData((prev) => ({ ...prev, privacy: val }))}
                    options={[
                      {
                        value: 'public',
                        label: 'Public',
                        icon: (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 3.03v.568c0 .334.148.65.405.864a11.944 11.944 0 015.725 2.795c.21.047.42-.012.577-.149" />
                          </svg>
                        ),
                      },
                      {
                        value: 'private',
                        label: 'Private',
                        icon: (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                          </svg>
                        ),
                      },
                    ]}
                  />

                  <div>
                    <label htmlFor="addressVisibility" className="block text-body-sm font-semibold text-surface-700 mb-2">
                      Address Visibility
                    </label>
                    <select
                      id="addressVisibility"
                      name="addressVisibility"
                      value={formData.addressVisibility}
                      onChange={handleChange}
                      className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                    >
                      <option value="public">Public</option>
                      <option value="attendants">Only attendants</option>
                      <option value="attendantsDay">Attendants (day-of only)</option>
                    </select>
                  </div>

                  <div className="relative">
                    <textarea
                      id="customTags"
                      name="customTags"
                      rows={2}
                      value={formData.customTags}
                      onChange={handleChange}
                      placeholder=" "
                      className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-5 pb-2 px-4 focus:ring-2 focus:bg-white focus:outline-none focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-body placeholder-transparent resize-none"
                    />
                    <label
                      htmlFor="customTags"
                      className="absolute left-4 transition-all duration-200 pointer-events-none select-none peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400 peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600 top-2 text-caption text-surface-500"
                    >
                      Custom Tags (comma separated)
                    </label>
                  </div>
                </section>
              </motion.div>
            )}

            {/* ============================== */}
            {/* STEP 3: Review                 */}
            {/* ============================== */}
            {step === 3 && (
              <motion.div
                key="step-3"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <h1 className="text-h1 text-surface-900">Review Your CliQQ</h1>
                  <p className="text-body text-surface-500 mt-1">Double-check everything before publishing</p>
                </div>

                {/* Basics Review */}
                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-h3 text-surface-900">Basics</h2>
                    <button type="button" onClick={() => setStep(0)} className="text-body-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors">
                      Edit
                    </button>
                  </div>
                  <div className="divide-y divide-surface-100">
                    <ReviewField label="Title" value={formData.title} onEdit={() => setStep(0)} />
                    <ReviewField label="Description" value={formData.description} onEdit={() => setStep(0)} />
                    <ReviewField label="Category" value={getCategoryName(formData.categoryId)} onEdit={() => setStep(0)} />
                    <ReviewField label="Image" value={formData.imageUrl || formData.imageUrls} onEdit={() => setStep(0)} />
                  </div>
                </section>

                {/* When & Where Review */}
                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-h3 text-surface-900">When & Where</h2>
                    <button type="button" onClick={() => setStep(1)} className="text-body-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors">
                      Edit
                    </button>
                  </div>
                  <div className="divide-y divide-surface-100">
                    <ReviewField label="Start" value={formatReviewDate(formData.startTime)} onEdit={() => setStep(1)} />
                    <ReviewField label="End" value={formatReviewDate(formData.endTime)} onEdit={() => setStep(1)} />
                    {formData.isOnline ? (
                      <ReviewField label="Location" value="Online event" onEdit={() => setStep(1)} />
                    ) : (
                      <>
                        <ReviewField label="Location" value={formData.location} onEdit={() => setStep(1)} />
                        <ReviewField
                          label="Address"
                          value={[formData.address, formData.city, formData.state, formData.zipCode].filter(Boolean).join(', ')}
                          onEdit={() => setStep(1)}
                        />
                      </>
                    )}
                  </div>
                </section>

                {/* Details Review */}
                <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-h3 text-surface-900">Details</h2>
                    <button type="button" onClick={() => setStep(2)} className="text-body-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors">
                      Edit
                    </button>
                  </div>
                  <div className="divide-y divide-surface-100">
                    <ReviewField label="Max Attendees" value={formData.maxAttendees || 'Unlimited'} onEdit={() => setStep(2)} />
                    <ReviewField label="Privacy" value={formData.privacy === 'public' ? 'Public' : 'Private'} onEdit={() => setStep(2)} />
                    <ReviewField label="Address Visibility" value={formData.addressVisibility} onEdit={() => setStep(2)} />
                    <ReviewField label="Tags" value={formData.customTags} onEdit={() => setStep(2)} />
                  </div>
                </section>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 pb-8">
            <div>
              {step > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 text-body-sm font-semibold text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-surface-300/40 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Back
                </button>
              )}
              {step === 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-5 py-2.5 text-body-sm font-semibold text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-surface-300/40 transition-colors duration-200"
                >
                  Cancel
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {step === 0 && (
                <Link
                  to="/create-group-cliqq"
                  className="px-4 py-2.5 text-body-sm font-semibold text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 transition-colors duration-200"
                >
                  Group CliQQ
                </Link>
              )}

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Create CliQQ
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEvent;
